import React, { useState, useEffect } from 'react';
import DataTable from '../../../components/ui/DataTable';
import { orderingQuotationService } from '../../ordering/services/orderingQuotationService';
import { Eye, Download, Check, X } from 'lucide-react';
import { Modal } from '../../../components/ui';
import { Button } from '../../../components/ui/Button';
import toast from 'react-hot-toast';
import { downloadPdfFromUrl } from '../../ordering/utils/downloadPdf';
import { PdfViewer } from '../../ordering/components/pdfviewer';

export const AppliancesTable = ({ search = "", filters = {} }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Approval modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  const [actionType, setActionType] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // PDF View state
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedPdfDoc, setSelectedPdfDoc] = useState(null);

  const fetchQuotations = async () => {
    setLoading(true);
    try {
      const res = await orderingQuotationService.getQuotations();
      if (res.success && res.data) {
        // Filter: only show confirmed orders (isConfirmOrder true) that have an approvalStatus set
        const filteredData = res.data.filter(q => q.isConfirmOrder === true && q.approvalStatus);
        setData(filteredData);
      }
    } catch (error) {
      console.error("Failed to fetch quotations:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuotations();
  }, []);

  const filteredData = React.useMemo(() => {
    let result = [...data];

    if (search) {
      const term = search.toLowerCase();
      result = result.filter(q =>
        (q.projectName || '').toLowerCase().includes(term) ||
        (q.quotationNo || '').toLowerCase().includes(term) ||
        (q.site || '').toLowerCase().includes(term) ||
        (q.referenceDesigner || '').toLowerCase().includes(term)
      );
    }

    const { date, fromDate, toDate, status } = filters;

    if (status && status !== "all") {
      result = result.filter(q => String(q.approvalStatus || 'Pending').toLowerCase() === String(status).toLowerCase());
    }

    if (date !== "all" || fromDate || toDate) {
      let startDate = null;
      let endDate = null;

      const now = new Date();
      if (date && date !== "all") {
        startDate = new Date();
        switch (date) {
          case "today": startDate.setHours(0, 0, 0, 0); break;
          case "this_week": case "week": startDate.setDate(now.getDate() - 7); break;
          case "this_month": case "month": startDate.setDate(now.getDate() - 30); break;
          case "this_year": case "year": startDate.setDate(now.getDate() - 365); break;
        }
        endDate = now;
      } else {
        if (fromDate) startDate = new Date(fromDate);
        if (toDate) endDate = new Date(toDate);
      }

      if (startDate || endDate) {
        result = result.filter((q) => {
          const createdAt = new Date(q.date || q.createdAt);
          if (startDate && createdAt < startDate) return false;
          if (endDate) {
            const end = new Date(endDate);
            end.setHours(23, 59, 59, 999);
            if (createdAt > end) return false;
          }
          return true;
        });
      }
    }

    return result;
  }, [data, search, filters]);

  const handleActionClick = (quotation, type) => {
    setSelectedQuotation(quotation);
    setActionType(type);
    setIsModalOpen(true);
  };

  const handleConfirmAction = async () => {
    if (!selectedQuotation) return;
    
    setActionLoading(true);
    try {
      const newStatus = actionType === 'approve' ? 'Approved' : 'Rejected';
      const res = await orderingQuotationService.updateStatus(selectedQuotation._id, newStatus);
      if (res.success) {
        toast.success(`Quotation ${newStatus.toLowerCase()} successfully`);
        setIsModalOpen(false);
        fetchQuotations();
      } else {
        toast.error(`Failed to ${actionType} quotation`);
      }
    } catch (err) {
      toast.error(`Error processing request`);
      console.error(err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleViewPdf = (row) => {
    if (row.pdfUrl) {
      setSelectedPdfDoc({
        fileUrl: row.pdfUrl,
        title: `Quotation_${row.quotationNo || 'VK'}.pdf`
      });
      setIsViewModalOpen(true);
    }
  };

  const columns = [
    { header: 'Project Name', accessor: 'projectName' },
    { header: 'Quotation No', accessor: 'quotationNo' },
    { header: 'Category', accessor: (row) => row.category || 'Appliances' },
    { header: 'Site', accessor: 'site' },
    { header: 'Designer', accessor: 'referenceDesigner' },
    { header: 'Created Date', accessor: 'date' },
    { 
      header: 'Grand Total', 
      accessor: (row) => row.totals?.grandTotal 
        ? `₹${Number(row.totals.grandTotal).toLocaleString('en-IN')}` 
        : '-' 
    },
    {
      header: 'Approval Status',
      accessor: (row) => (
        <span className={`px-2.5 py-1 border rounded-md text-[11px] font-semibold ${
          row.approvalStatus === 'Approved' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
          row.approvalStatus === 'Rejected' ? 'bg-red-50 text-red-700 border-red-200' :
          'bg-amber-50 text-amber-700 border-amber-200'
        }`}>
          {row.approvalStatus || 'Pending'}
        </span>
      )
    },
    {
      header: 'Actions',
      accessor: (row) => (
        <div className="flex items-center gap-3">
          {(!row.approvalStatus || row.approvalStatus === 'Pending') && (
            <>
              <button
                onClick={() => handleActionClick(row, "approve")}
                className="p-1 text-emerald-600 hover:bg-emerald-50 rounded transition-colors"
                title="Approve Quotation"
              >
                <Check size={18} strokeWidth={2.5} />
              </button>
              <button
                onClick={() => handleActionClick(row, "reject")}
                className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                title="Reject Quotation"
              >
                <X size={18} strokeWidth={2.5} />
              </button>
              <div className="w-px h-4 bg-slate-300"></div>
            </>
          )}

          {row.pdfUrl && (
            <button 
              onClick={() => handleViewPdf(row)} 
              className="text-slate-400 hover:text-brand-blue transition-colors" 
              title="View PDF"
            >
              <Eye size={18} />
            </button>
          )}
          
          {row.approvalStatus === 'Approved' && row.pdfUrl && (
            <button onClick={() => downloadPdfFromUrl(row.pdfUrl, `Quotation_${row.quotationNo || 'VK'}.pdf`)} className="text-slate-400 hover:text-brand-blue transition-colors" title="Download PDF">
              <Download size={18} />
            </button>
          )}
        </div>
      )
    }
  ];

  return (
    <>
      <div className="bg-white rounded-xl shadow-sm border border-slate-200">
        <DataTable 
          columns={columns} 
          data={filteredData}
          isLoading={loading}
          loadingText="Loading Appliance Orders..."
          emptyMessage="No ordering quotations found."
        />
      </div>
      
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={`${actionType === 'approve' ? 'Approve' : 'Reject'} Quotation`}
        className="max-w-md w-full"
      >
        <div className="py-4">
          <p className="text-sm text-slate-600 mb-6">
            Are you sure you want to {actionType} quotation <strong>{selectedQuotation?.quotationNo}</strong>?
            This action cannot be undone.
          </p>
          <div className="flex gap-3 justify-end mt-2">
            <Button
              variant="secondary"
              onClick={() => setIsModalOpen(false)}
              className="text-slate-600 hover:bg-slate-100 font-medium w-24"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirmAction}
              disabled={actionLoading}
              className={`w-28 font-medium shadow-sm !text-white ${
                actionType === 'approve' 
                  ? '!bg-emerald-600 hover:!bg-emerald-700 !border-emerald-600' 
                  : '!bg-red-600 hover:!bg-red-700 !border-red-600'
              }`}
            >
              {actionLoading ? 'Processing...' : `Yes, ${actionType}`}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal 
        isOpen={isViewModalOpen} 
        onClose={() => setIsViewModalOpen(false)}
        title={selectedPdfDoc?.title || "Quotation Document"}
        className="max-w-6xl w-full"
      >
        <div className="-m-5 sm:-m-6 h-[85vh] flex flex-col rounded-b-lg sm:rounded-b-xl overflow-hidden">
          <PdfViewer 
            selectedDocument={selectedPdfDoc} 
            onClose={() => setIsViewModalOpen(false)} 
          />
        </div>
      </Modal>
    </>
  );
};
